/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

interface UseFloatingDropdownMenuOptions {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  minWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  viewportPadding?: number;
  gap?: number;
  zIndex?: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function useFloatingDropdownMenu<TDropdown extends HTMLElement = HTMLElement>({
  open,
  anchorRef,
  minWidth = 0,
  minHeight = 96,
  maxHeight = 320,
  viewportPadding = 8,
  gap = 4,
  zIndex = 10000,
}: UseFloatingDropdownMenuOptions) {
  const dropdownRef = useRef<TDropdown | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 0,
    visibility: 'hidden',
    zIndex,
  });

  const updateDropdownPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      setDropdownStyle((current) => ({ ...current, visibility: 'hidden' }));
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const dropdownRect = dropdownRef.current?.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxUsableWidth = Math.max(0, viewportWidth - viewportPadding * 2);
    const desiredWidth = Math.max(rect.width, minWidth);
    const width = Math.min(desiredWidth, maxUsableWidth);
    const availableBelow = viewportHeight - rect.bottom - viewportPadding - gap;
    const availableAbove = rect.top - viewportPadding - gap;
    const measuredHeight =
      dropdownRect?.height && dropdownRect.height > 0 ? Math.min(dropdownRect.height, maxHeight) : maxHeight;
    const openAbove = availableBelow < measuredHeight && availableAbove > availableBelow;
    const availableHeight = Math.max(48, Math.floor(openAbove ? availableAbove : availableBelow));
    const boundedHeight = Math.min(maxHeight, availableHeight);
    const boundedMinHeight = Math.min(minHeight, boundedHeight);
    const left = clamp(rect.left, viewportPadding, Math.max(viewportPadding, viewportWidth - viewportPadding - width));

    setDropdownStyle({
      position: 'fixed',
      // Reset the opposite CSS inset explicitly: portal menus may inherit a
      // default top:100%, which otherwise wins over bottom when opening above.
      top: openAbove ? 'auto' : rect.bottom + gap,
      bottom: openAbove ? viewportHeight - rect.top + gap : 'auto',
      left,
      width,
      minHeight: boundedMinHeight,
      maxHeight: boundedHeight,
      visibility: 'visible',
      zIndex,
    });
  }, [anchorRef, gap, maxHeight, minHeight, minWidth, viewportPadding, zIndex]);

  useLayoutEffect(() => {
    if (!open) return;

    updateDropdownPosition();
    const animationFrameId = window.requestAnimationFrame(updateDropdownPosition);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateDropdownPosition)
        : null;
    const anchor = anchorRef.current;
    if (anchor) resizeObserver?.observe(anchor);
    if (dropdownRef.current) resizeObserver?.observe(dropdownRef.current);

    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [anchorRef, open, updateDropdownPosition]);

  useEffect(() => {
    if (open) return;
    setDropdownStyle((current) => ({ ...current, visibility: 'hidden' }));
  }, [open]);

  return {
    dropdownRef,
    dropdownStyle,
    updateDropdownPosition,
  };
}
